import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAppSelector } from "../app/hooks";
import { selectResourceId, selectUserName, selectRole, userStatus, selectGrade } from "../app/auth/createAuthSlice";
import { IoIosArrowForward } from "react-icons/io";
import ProgressBar from "./ProgressBar";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useGetProfileQuery } from "../app/appApiSlice";
import { getCurrentUser } from "../app/msgraphapi/graphService";

ChartJS.register(ArcElement, Tooltip, Legend);

type UserProfileProps = {
  collapsed?: boolean;
  onLogout?: () => void;
};

const UserProfile: React.FC<UserProfileProps> = ({ collapsed = false, onLogout }) => {
  const resourceId = useAppSelector(selectResourceId);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentUserUrl, setCurrentUserUrl] = useState<string | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);
  const userRole = useAppSelector(selectGrade);
  const status = useAppSelector(userStatus)?.trim().toLowerCase(); 
  const userName = useAppSelector(selectUserName);
  const { data: profileData, isLoading: isProfileLoadingLoading, error: profileError, fulfilledTimeStamp } = useGetProfileQuery(resourceId!, { skip: !resourceId });
  const progress = profileData?.data?.profileCompletionPercentage || 0;
  const navigate = useNavigate();

  const profilePicSrc = profileData?.data?.profilePic
    ? profileData.data.profilePic.startsWith("data:")
      ? profileData.data.profilePic
      : `${profileData.data.profilePic}${profileData.data.profilePic.includes('?') ? '&' : '?'}t=${fulfilledTimeStamp}`
    : "/assets/img/default-user.jpg";

  const doughnutData = {
    datasets: [
      {
        data: [progress, 100 - progress],
        backgroundColor: ["#26890D", "#fff"],
        borderWidth: 0,
        cutout: "82%",
      },
    ],
  };

  const doughnutOptions = {
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    maintainAspectRatio: false,
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleLogout = () => {
    setIsMenuOpen(false);
    sessionStorage.removeItem("alumniPortal.roleView");
    navigate("/");

    if (onLogout) {
      onLogout();
    }
  };

  useEffect(() => {
    getCurrentUser()
    .then((res) => {   
      setCurrentUserUrl(res.pictureUrl);    
    }) 
  }, []);

  if (collapsed) {
    return (
      <div className="m-2 mb-3 p-3 pb-0 flex flex-col items-center gap-3"> 
        <img
          src={status === "active" ? (currentUserUrl || "/assets/img/default-user.jpg") : profilePicSrc}
          alt="User Profile"
          className="w-10 h-10"
        />

        {/* Doughnut Chart */}
        {
          status == "active" ? null :
          <div className="relative w-10 h-10">
            <Doughnut data={doughnutData} options={doughnutOptions} />
            <div className="absolute inset-0 flex items-center justify-center text-xs">
              {progress}%
            </div>
          </div>
        }
      </div>
    );
  }
  
  return (
    <div className="m-2 border border-[#63666A] p-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          <img className="w-10 h-10" src={status === "active" ? (currentUserUrl || "/assets/img/default-user.jpg") : profilePicSrc} alt="User Profile" />
          <div className=" ">
            <div className="text-sm font-semibold">{ status === "active" ? userName : profileData?.data?.name }</div>
            <div className="text-[#BBBCBC] text-xs">{ status === "active" ? userRole : profileData?.data?.currentJobTitle }</div>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="text-sm"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label="Open profile actions"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
          >
            <HiOutlineDotsVertical className="text-xs cursor-pointer" />
          </button>

          {isMenuOpen && (
            <div className="fixed left-[240px] top-18 z-20">
              <div className="absolute top-2.5 -left-1 h-3 w-3 -rotate-45 border-l border-t border-[#222] bg-[#222]" />
              <div className="min-w-30!  bg-[#222] py-2">
                {
                  status === "active" ? null :
                  <NavLink
                    to="/profile"
                    state={{ resourceId }}
                    className="flex items-center justify-between px-3 py-1"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    View Profile
                  </NavLink>
                }
                <button
                  type="button"
                  className="w-full px-3 py-1 text-left "
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
       <div className="mt-4">
            <ProgressBar value={progress} label={`${progress}% profile complete`} />
          </div> 
    </div>
  );
};

export default UserProfile;